import React from 'react';
import { Modal, ModalActions, ModalContent } from '../../../Modal';
import { AsyncButton } from '../../../Button';
import { useAddPublicICSSubscription, useDeletePublicICSSubscription, usePublicICSSubscriptionQuery } from '@uplift/queries';
import { Box, Divider, Tab, Tabs, Typography } from '@mui/material';
import { useIsMobile } from '@uplift/hooks';
import { InstructionAppleCalendar, InstructionGoogleCalendar, InstructionNativeCalendarWithLinkButton } from './Instructions.component';
import { Alert } from '../../../Alert';


export type PublicICSSubscriptionModalProps = {
  open: boolean;
  onClose: () => void;
};


export const PublicICSSubscriptionModal: React.FC<PublicICSSubscriptionModalProps> = (props) => {
  const isMobile = useIsMobile();
  const { data: publicIcsSubscription, isLoading } = usePublicICSSubscriptionQuery({ enabled: props.open });
  const deleteICSSubscriptionMutation = useDeletePublicICSSubscription();
  const addICSSubscriptionMutation = useAddPublicICSSubscription();

  const [ tab, setTab ] = React.useState(() => {
    if (isMobile) return 'native-calendar';

    return 'google-calendar';
  });

  return (
    <Modal fullScreen={isMobile} open={props.open} onClose={props.onClose} title="Subscribe to calendar" maxWidth="md" fullWidth loading={isLoading}>
      <ModalContent noPadding dividers>
        <Box display="flex" flexDirection="column">
          {publicIcsSubscription?.subscriptionUrl ? (
            <>
              <Tabs
                orientation={isMobile ? 'vertical' : 'horizontal'}
                indicatorColor="secondary"
                textColor="secondary"
                value={tab}
                onChange={(e, newValue) => setTab(newValue)} variant="fullWidth"
              >
                <Tab value="google-calendar" label="Google Calendar" color="secondary" />
                <Tab value="apple-calendar" label="Apple Calendar" color="secondary" />
                <Tab value="native-calendar" label="Quick Subscription" color="secondary" />
              </Tabs>
              {isMobile && <Divider />}
              <Box px={3} py={2}>
                {tab === 'google-calendar' && <InstructionGoogleCalendar icsLink={publicIcsSubscription.subscriptionUrl} />}
                {tab === 'apple-calendar' && <InstructionAppleCalendar icsLink={publicIcsSubscription.subscriptionUrl} />}
                {tab === 'native-calendar' && <InstructionNativeCalendarWithLinkButton icsLink={publicIcsSubscription.directSubscriptionUrl} />}
              </Box>
            </>
          ) : (
            <Typography sx={{ p: 3 }}>Enable calendar subscription to get the .ics link and instructions.</Typography>
          )}
        </Box>
      </ModalContent>
      <ModalActions>
        {publicIcsSubscription?.subscriptionUrl ? (
          <Box display="flex" flex={1} alignItems="center" flexDirection="column" gap={2} boxSizing="border-box">
            <Alert severity="info" sx={{ flex: 1, width: '100%' }}>
              When you will disable subscription, subscription link will stop working and you will need to re-enable it to get a new link. And subscribe to the calendar with a new link.
            </Alert>
            <AsyncButton fullWidth variant="contained" color="error" onClick={() => deleteICSSubscriptionMutation.mutateAsync()}>Disable subscription</AsyncButton>
          </Box>
        ) : (
          <AsyncButton variant="contained" color="secondary" onClick={() => addICSSubscriptionMutation.mutateAsync()}>Enable subscription</AsyncButton>
        )}
      </ModalActions>
    </Modal>
  );
};
